// CompanySelector.js - Company selection dropdown for multi-company support
import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  CircularProgress,
  Typography,
  Avatar,
  Tooltip,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useCompany } from '../contexts/CompanyContext';

// Routes where the user is inside a full-page form / wizard / payroll workspace.
// Changing company mid-form would silently swap the data context, so the
// selector is disabled there to prevent accidental switches.
const LOCKED_ROUTE_PATTERNS = [
  /^\/payroll\/calculator/,     // AttendanceSalaryCalculator payroll workspace
  /^\/employees\/new$/,         // EmployeeFormPage (create wizard)
  /^\/employees\/[^/]+\/edit$/, // EmployeeFormPage (edit)
  /^\/expense-claims\/submit$/, // SubmitClaimWizard
];

const CompanySelector = ({ variant = 'header' }) => {
  const isSidebar = variant === 'sidebar';
  const location = useLocation();
  const disabled = LOCKED_ROUTE_PATTERNS.some((pattern) => pattern.test(location.pathname));
  const {
    companies,
    selectedCompanyId,
    loading,
    switchCompany,
    canViewAllCompanies,
    ALL_COMPANIES,
  } = useCompany();

  // Still loading companies from Supabase
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  // Don't show selector if user has 0 or 1 company (no meaningful choice to make)
  if (companies.length <= 1) {
    return null;
  }

  const getCompanyCode = (company) => {
    if (company?.short_code) return company.short_code.toUpperCase();
    const fallback = company?.name?.replace(/[^a-zA-Z]/g, '') || '';
    return fallback.slice(0, 2).toUpperCase() || '—';
  };

  const isLongName = (name) => (name || '').length > 18;

  const CompanyAvatar = ({ company, isHeader }) => (
    <Avatar
      sx={{
        width: 24,
        height: 24,
        borderRadius: '6px',
        fontSize: '0.7rem',
        fontWeight: 700,
        flexShrink: 0,
        bgcolor: isHeader ? 'rgba(255,255,255,0.9)' : '#e2e8f0',
        color: isHeader ? 'primary.dark' : '#475569',
      }}
    >
      {getCompanyCode(company)}
    </Avatar>
  );

  const menuProps = {
    PaperProps: {
      sx: {
        minWidth: 240,
        maxWidth: 420,
      },
    },
  };

  const renderValue = (selected) => {
    if (selected === ALL_COMPANIES) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
          <Avatar
            sx={{
              width: 24,
              height: 24,
              borderRadius: '6px',
              fontSize: '0.7rem',
              fontWeight: 700,
              flexShrink: 0,
              bgcolor: isSidebar ? '#e2e8f0' : 'rgba(255,255,255,0.9)',
              color: isSidebar ? '#475569' : 'primary.dark',
            }}
          >
            ALL
          </Avatar>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              color: isSidebar ? '#334155' : 'white',
            }}
          >
            All Companies
          </Typography>
          <Chip
            label={`${companies.length}`}
            size="small"
            variant="outlined"
            sx={{
              height: 18,
              fontSize: '0.7rem',
              color: isSidebar ? '#64748b' : 'white',
              borderColor: isSidebar ? '#cbd5e1' : 'rgba(255,255,255,0.5)',
              flexShrink: 0,
            }}
          />
        </Box>
      );
    }
    const company = companies.find(c => c.id === selected);
    if (!company) return '';
    const companyName = company.name || '';
    const buttonContent = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
        <CompanyAvatar company={company} isHeader={!isSidebar} />
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: isLongName(companyName) ? 'ellipsis' : 'unset',
            whiteSpace: 'nowrap',
            flex: 1,
            color: isSidebar ? '#334155' : 'white',
          }}
        >
          {companyName}
        </Typography>
      </Box>
    );

    if (isLongName(companyName)) {
      return <Tooltip title={companyName} arrow>{buttonContent}</Tooltip>;
    }
    return buttonContent;
  };

  if (isSidebar) {
    return (
      <FormControl variant="standard" fullWidth>
        <Select
          value={selectedCompanyId}
          onChange={(e) => switchCompany(e.target.value)}
          disabled={disabled}
          disableUnderline
          displayEmpty
          MenuProps={menuProps}
          sx={{
            color: '#334155',
            borderRadius: '6px',
            bgcolor: '#f8fafc',
            border: '1px solid #e2e8f0',
            transition: 'all 0.2s',
            '&:hover': { bgcolor: '#f1f5f9', borderColor: '#cbd5e1' },
            '&.Mui-focused': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' },
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 1,
              px: 2,
              pr: '32px !important',
            },
            '& .MuiSvgIcon-root': {
              color: '#64748b',
              right: 10,
            },
          }}
          renderValue={renderValue}
        >
          {canViewAllCompanies() && (
            <MenuItem value={ALL_COMPANIES}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Avatar
                  sx={{ width: 24, height: 24, borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700, bgcolor: '#e2e8f0', color: '#475569' }}
                >
                  ALL
                </Avatar>
                <strong>All Companies</strong>
                <Chip
                  label={`${companies.length} companies`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </MenuItem>
          )}
          {[...companies].sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return dateA - dateB;
          }).map((company) => (
            <MenuItem key={company.id} value={company.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <CompanyAvatar company={company} isHeader={false} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {company.name}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // Header variant
  return (
    <FormControl
      variant="outlined"
      size="small"
      sx={{
        minWidth: { xs: 120, sm: 180, md: 220 },
        maxWidth: { xs: 220, sm: 280, md: 340 },
      }}
    >
      <InputLabel
        id="company-selector-label"
        sx={{
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-focused': { color: 'white' },
          fontSize: { xs: '0.7rem', sm: '0.875rem' },
        }}
      >
        Company
      </InputLabel>
      <Select
        labelId="company-selector-label"
        id="company-selector"
        value={selectedCompanyId}
        onChange={(e) => switchCompany(e.target.value)}
        disabled={disabled}
        label="Company"
        MenuProps={menuProps}
        sx={{
          color: 'white',
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'white',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'white',
          },
          '.MuiSvgIcon-root': {
            color: 'white',
          },
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }
        }}
        renderValue={renderValue}
      >
        {canViewAllCompanies() && (
          <MenuItem value={ALL_COMPANIES}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Avatar
                sx={{ width: 24, height: 24, borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700, bgcolor: '#e2e8f0', color: '#475569' }}
              >
                ALL
              </Avatar>
              <strong>All Companies</strong>
              <Chip
                label={`${companies.length} companies`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </MenuItem>
        )}
        {[...companies].sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateA - dateB;
        }).map((company) => (
          <MenuItem key={company.id} value={company.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <CompanyAvatar company={company} isHeader={false} />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {company.name}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CompanySelector;
